import { useEffect, useRef, useState } from 'react';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Margin, usePDF } from 'react-to-pdf';
import { useReactToPrint } from 'react-to-print';
import '../venueSchedule.scss';
import dayjs from 'dayjs';
import { Watermark } from 'antd';
import { ScheduleData, ScheduleMetadata } from '../../../../../helpers/venueScheduleHelper';
import MlxToolTip from '../../../../../components/common/MlxToolTip/MlxToolTip';
import RoleBasedShiftScheduler from './RoleBasedShiftScheduler';

type PrintPreviewProps = {
    setShowPrintPreview: React.Dispatch<React.SetStateAction<boolean>>;
    scheduleDialogRef: React.MutableRefObject<any>;
    staffsWithSchedule: ScheduleData | null;
    scheduleMetadata: ScheduleMetadata | null;
    selectedWeek: dayjs.Dayjs;
    amPmView?: 'amPm' | 'normal';
};

const PrintPreview = ({
    setShowPrintPreview,
    scheduleDialogRef,
    staffsWithSchedule,
    scheduleMetadata,
    selectedWeek,
    amPmView,
}: Readonly<PrintPreviewProps>) => {
    const contentToPrintRef = useRef(null);
    const { toPDF, targetRef: contentToPDFRef } = usePDF({
        filename: 'Weekly_Staff_Shift_Schedule.pdf',
        method: 'save',
        page: { orientation: 'landscape', margin: Margin.SMALL },
    });

    const [selectedView, setSelectedView] = useState<string | null>('role-based');
    const [panelRefZIndex, setPanelRefZIndex] = useState<number | null>(null);
    const [viewType, setViewType] = useState<'amPm' | 'normal' | null>(amPmView ?? 'normal');

    const handlePrint = useReactToPrint({
        documentTitle: 'Weekly_Staff_Shift_Schedule',
        removeAfterPrint: true,
    });

    useEffect(() => {
        if (!scheduleDialogRef?.current?.getElement()?.parentElement) return;

        setPanelRefZIndex(scheduleDialogRef?.current?.getElement()?.parentElement?.style.zIndex);
    }, [scheduleDialogRef]);

    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.ctrlKey && e.key === 'p') {
                e.preventDefault();
                handlePrint(null, () => contentToPrintRef?.current);
            }
        };

        const handleBeforePrint = (e: Event) => {
            e.preventDefault();
            alert('Please use the Print button to print the schedule.');
        };

        window.addEventListener('keydown', handleKeyPress);
        window.addEventListener('beforeprint', handleBeforePrint);

        return () => {
            window.removeEventListener('keydown', handleKeyPress);
            window.removeEventListener('beforeprint', handleBeforePrint);
        };
    }, [handlePrint]);

    return (
        <div className="col-md-12 col-lg-12 mb-3 h-100 ">
            <div className="card h-100 ">
                <div className="card-header flex-grow-0">
                    <div className="d-flex align-items-center">
                        <div className="flex-grow-1 d-flex align-items-center">
                            <Dropdown
                                value={selectedView}
                                onChange={(e: DropdownChangeEvent) => {
                                    setSelectedView(e.value);
                                }}
                                options={[
                                    { name: 'Role Based View', code: 'role-based' },
                                    { name: 'Show All Staffs', code: 'all-staffs' },
                                ]}
                                optionLabel="name"
                                optionValue="code"
                                placeholder="Select View"
                                className="w-full md:w-14rem me-3"
                                pt={{
                                    panel: {
                                        style: {
                                            '--custom-dropdown-z-index': panelRefZIndex ?? 1056,
                                        },
                                    },
                                }}
                            />

                            <Dropdown
                                value={viewType}
                                onChange={(e: DropdownChangeEvent) => {
                                    setViewType(e.value);
                                }}
                                options={[
                                    { name: 'Normal', code: 'normal' },
                                    { name: 'AM/PM', code: 'amPm' },
                                ]}
                                optionLabel="name"
                                optionValue="code"
                                placeholder="Select View Type"
                                className="w-full md:w-14rem me-3"
                                pt={{
                                    panel: {
                                        style: {
                                            '--custom-dropdown-z-index': panelRefZIndex ?? 1056,
                                        },
                                    },
                                }}
                            />
                        </div>
                        <div className="flex-grow-0 me-4 ms-1 gap-2 d-flex">
                            <MlxToolTip target=".pdf-icon">
                                <div className="cldr-action-icon-large">
                                    <i
                                        className="pi pi-file-pdf pdf-icon"
                                        data-pr-tooltip="Export to PDF"
                                        data-pr-position="left"
                                        onClick={() => toPDF()}
                                        role="button"
                                    ></i>
                                </div>
                            </MlxToolTip>
                            <MlxToolTip target=".print-icon">
                                <div className="cldr-action-icon-large">
                                    <i
                                        className="pi pi-print print-icon"
                                        data-pr-tooltip="Print Schedule"
                                        data-pr-position="left"
                                        onClick={() => {
                                            handlePrint(null, () => contentToPrintRef.current);
                                        }}
                                        role="button"
                                    ></i>
                                </div>
                            </MlxToolTip>
                            <div
                                className="icon ms-5 align-items-center justify-content-start d-flex border border-primary rounded-circle p-2"
                                onClick={() => {
                                    setShowPrintPreview(false);
                                }}
                                role="button"
                            >
                                <i className="ri-fullscreen-exit-line print-icon icon" />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card-body flex-grow-1">
                    <div className="survey-section h-100">
                        <Watermark content={staffsWithSchedule?.schedule?.actionStatus !== 'published' ? 'DRAFT' : ''}>
                            <RoleBasedShiftScheduler
                                selectedWeek={selectedWeek}
                                staffsWithSchedule={staffsWithSchedule}
                                scheduleMetadata={scheduleMetadata}
                                selectedView={selectedView}
                                viewType={viewType ?? 'normal'}
                            />
                        </Watermark>
                    </div>
                </div>

                {/* Rendering a table for printing and pdf export purposes */}
                <div style={{ position: 'absolute', zIndex: '-100', height: 'auto' }}>
                    <style type="text/css" media="print">
                        {`
                        @page {
                            size: landscape;
                        }

                        @media print {
                            body {
                                zoom: 80%;
                            }
                            // table {page-break-inside: avoid;}
                        }
                            
                        .datatable-role-name {
                            display: ${viewType === 'amPm' && selectedView === 'role-based' ? 'none' : 'block'};
                        }
                    `}
                    </style>
                    <div ref={contentToPrintRef}>
                        <div ref={contentToPDFRef}>
                            <div className="card-body flex-grow-1">
                                <div className="survey-section h-100">
                                    <Watermark content={staffsWithSchedule?.schedule?.actionStatus !== 'published' ? 'DRAFT' : ''}>
                                        <RoleBasedShiftScheduler
                                            selectedWeek={selectedWeek}
                                            staffsWithSchedule={staffsWithSchedule}
                                            scheduleMetadata={scheduleMetadata}
                                            selectedView={selectedView}
                                            viewType={viewType ?? 'normal'}
                                        />
                                    </Watermark>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrintPreview;
